import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  OnboardingData,
  OnboardingMeta,
  OnboardingStep,
} from "@/types/onboarding.types";
import {
  CompanySize,
  ReferralSource,
  OnboardingProfile,
} from "@/types/onboarding.types";
import { onboardingService } from "@/services/onboardingService";

interface OnboardingContextType {
  data: OnboardingData;
  currentStep: number;
  totalSteps: number;
  isLoading: boolean;
  isNavigating: boolean;
  updateData: (
    stepData: Partial<OnboardingData>,
    advanceStep?: boolean,
  ) => Promise<void>;
  nextStep: () => Promise<void>;
  previousStep: () => Promise<void>;
  completeOnboarding: () => Promise<void>;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(
  undefined,
);

interface OnboardingProviderProps {
  children: React.ReactNode;
}

// Define the steps configuration - updated to match ModernOnboardingFlow
const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: "welcome",
    title: "Welcome",
    description: "Welcome to Hubflo Agents",
    stepNumber: 1,
    isRequired: true,
    canSkip: false,
  },
  {
    id: "industry",
    title: "Industry",
    description: "What industry is your business in?",
    stepNumber: 2,
    isRequired: true,
    canSkip: false,
  },
  {
    id: "crm_systems",
    title: "CRM Systems",
    description: "What CRM/ticketing system do you use?",
    stepNumber: 3,
    isRequired: true,
    canSkip: false,
  },
  {
    id: "daily_contact_volume",
    title: "Contact Volume",
    description: "How many contacts do you receive daily?",
    stepNumber: 4,
    isRequired: true,
    canSkip: false,
  },
  {
    id: "website",
    title: "Website",
    description: "Your website URL",
    stepNumber: 5,
    isRequired: true,
    canSkip: false,
  },
  {
    id: "branding",
    title: "Branding",
    description: "Set up your brand colors and logo",
    stepNumber: 6,
    isRequired: true,
    canSkip: false,
  },
  {
    id: "persona_selection",
    title: "Persona Selection",
    description: "Choose your first AI employee",
    stepNumber: 7,
    isRequired: true,
    canSkip: false,
  },
];

export function OnboardingProvider({ children }: OnboardingProviderProps) {
  const { organization, refreshProfile } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const totalSteps = ONBOARDING_STEPS.length;

  const [data, setData] = useState<OnboardingData>({
    referral_source: undefined,
    onboarding_profile: undefined,
    onboarding_meta: {
      completed: false,
      current_step: 1,
      completed_steps: [],
    },
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  // Derive currentStep from data instead of separate state
  const currentStep = data.onboarding_meta?.current_step || 1;

  // Memoize the organization data transformation to avoid expensive operations
  const transformedOrgData = useMemo(() => {
    if (!organization) return null;

    // Type-safe access to organization properties with proper typing
    // Handle the fact that organization comes from the database with Json types
    interface OrganizationWithOnboarding {
      referral_source?: string;
      onboarding_profile?: unknown; // Json type from database
      onboarding_meta?: unknown; // Json type from database
    }

    const orgData = organization as unknown as OrganizationWithOnboarding;

    return {
      referral_source: orgData.referral_source as ReferralSource | undefined,
      onboarding_profile: orgData.onboarding_profile as
        | OnboardingProfile
        | undefined,
      onboarding_meta: (orgData.onboarding_meta as OnboardingMeta) || {
        completed: false,
        current_step: 1,
        completed_steps: [],
      },
    };
  }, [organization]);

  // Load existing onboarding data when organization is available
  useEffect(() => {
    if (transformedOrgData) {
      setData(transformedOrgData);
    }
  }, [transformedOrgData]);

  // Memoize expensive operations
  const shouldRefreshProfile = useCallback(
    (stepData: Partial<OnboardingData>) => {
      return (
        stepData.referral_source !== undefined ||
        stepData.onboarding_profile !== undefined
      );
    },
    [],
  );

  const updateData = useCallback(
    async (stepData: Partial<OnboardingData>, advanceStep: boolean = false) => {
      if (!organization?.id) return;

      setIsLoading(true);
      try {
        // Use the service layer for database operations
        const result = await onboardingService.updateOnboardingData(
          organization.id,
          stepData,
          currentStep,
          totalSteps,
          advanceStep,
        );

        if (result.error) {
          throw result.error;
        }

        // Update local state with functional update to avoid race conditions
        setData((prevData) => ({
          ...prevData,
          ...stepData,
          onboarding_meta:
            result.data?.onboarding_meta || prevData.onboarding_meta,
        }));

        // Only refresh profile if we're saving actual data changes, not just navigation
        if (shouldRefreshProfile(stepData)) {
          await refreshProfile();
        }

        console.log("Onboarding data updated:", result.data);
      } catch (error) {
        console.error("Error updating onboarding data:", error);
        toast({
          variant: "destructive",
          title: "Error saving progress",
          description:
            error instanceof Error
              ? error.message
              : "Failed to save your progress. Please try again.",
        });
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [
      organization?.id,
      currentStep,
      totalSteps,
      refreshProfile,
      toast,
      shouldRefreshProfile,
    ],
  );

  const nextStep = useCallback(async () => {
    if (currentStep >= totalSteps || !organization?.id) return;

    setIsNavigating(true);
    const newStep = currentStep + 1;
    const newCompletedSteps = Array.from(
      new Set([...(data.onboarding_meta?.completed_steps || []), currentStep]),
    );

    // Optimistic UI update
    setData((prevData) => ({
      ...prevData,
      onboarding_meta: {
        ...prevData.onboarding_meta!,
        current_step: newStep,
        completed_steps: newCompletedSteps,
      },
    }));

    try {
      const result = await onboardingService.updateNavigationStep(
        organization.id,
        newStep,
        newCompletedSteps,
      );

      if (result.error) {
        throw result.error;
      }
    } catch (error) {
      console.error("Error updating step:", error);
      // Revert optimistic update using functional state update
      setData((prevData) => ({
        ...prevData,
        onboarding_meta: {
          ...prevData.onboarding_meta!,
          current_step: currentStep,
          completed_steps: data.onboarding_meta?.completed_steps || [],
        },
      }));
      toast({
        variant: "destructive",
        title: "Navigation Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to navigate to next step.",
      });
    } finally {
      setIsNavigating(false);
    }
  }, [
    currentStep,
    totalSteps,
    organization?.id,
    data.onboarding_meta?.completed_steps,
    toast,
  ]);

  const previousStep = useCallback(async () => {
    if (currentStep <= 1 || !organization?.id) return;

    setIsNavigating(true);
    const newStep = currentStep - 1;
    const completedSteps = data.onboarding_meta?.completed_steps || [];

    // Optimistic UI update
    setData((prevData) => ({
      ...prevData,
      onboarding_meta: {
        ...prevData.onboarding_meta!,
        current_step: newStep,
        completed_steps: completedSteps,
      },
    }));

    try {
      const result = await onboardingService.updateNavigationStep(
        organization.id,
        newStep,
        completedSteps,
      );

      if (result.error) {
        throw result.error;
      }
    } catch (error) {
      console.error("Error updating step:", error);
      // Revert optimistic update using functional state update
      setData((prevData) => ({
        ...prevData,
        onboarding_meta: {
          ...prevData.onboarding_meta!,
          current_step: currentStep,
          completed_steps: completedSteps,
        },
      }));
      toast({
        variant: "destructive",
        title: "Navigation Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to navigate to previous step.",
      });
    } finally {
      setIsNavigating(false);
    }
  }, [
    currentStep,
    organization?.id,
    data.onboarding_meta?.completed_steps,
    toast,
  ]);

  const completeOnboarding = useCallback(async () => {
    if (!organization?.id) return;

    setIsLoading(true);
    try {
      const result = await onboardingService.completeOnboarding(
        organization.id,
        totalSteps,
      );

      if (result.error) {
        throw result.error;
      }

      // Clear new user flags and preloaded data when onboarding is complete
      sessionStorage.removeItem("newUserOnboarding");
      sessionStorage.removeItem("pendingNewUserRedirect");
      sessionStorage.removeItem("preloadedIndustries");
      sessionStorage.removeItem("preloadedWelcomeForm");

      // Update local state with completion data
      setData((prevData) => ({
        ...prevData,
        onboarding_meta: result.data!,
      }));

      await refreshProfile();

      console.log("Onboarding completed:", result.data);

      // Redirect to payment page
      navigate("/payment");
    } catch (error) {
      console.error("Error completing onboarding:", error);
      toast({
        variant: "destructive",
        title: "Error completing onboarding",
        description:
          error instanceof Error
            ? error.message
            : "Failed to complete onboarding. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  }, [organization?.id, totalSteps, refreshProfile, navigate, toast]);

  // Reset onboarding on invalid step (error recovery)
  const resetOnboarding = useCallback(async () => {
    if (!organization?.id) return;

    try {
      const result = await onboardingService.resetOnboarding(organization.id);

      if (result.error) {
        throw result.error;
      }

      setData((prevData) => ({
        ...prevData,
        onboarding_meta: result.data!,
      }));
    } catch (error) {
      console.error("Error resetting onboarding:", error);
      toast({
        variant: "destructive",
        title: "Reset Error",
        description: "Failed to reset onboarding. Please refresh the page.",
      });
    }
  }, [organization?.id, toast]);

  // Handle invalid step values with proper error recovery
  useEffect(() => {
    // Only validate step if we have valid data and organization is loaded
    if (!organization?.id || isLoading || isNavigating) {
      return; // Skip validation during loading states
    }

    // More careful validation - only reset if step is clearly invalid
    // Allow for temporary intermediate states during async operations
    if (
      typeof currentStep !== "number" ||
      isNaN(currentStep) ||
      currentStep < 1 ||
      currentStep > totalSteps
    ) {
      console.warn(
        `Invalid onboarding step detected: ${currentStep}. Valid steps are 1-${totalSteps}. Attempting graceful recovery.`,
      );

      // More graceful recovery - try to determine correct step from data
      const completedSteps = data.onboarding_meta?.completed_steps || [];
      const lastCompletedStep = Math.max(0, ...completedSteps);
      const recoveryStep = Math.min(lastCompletedStep + 1, totalSteps);

      console.log(
        `Attempting recovery to step ${recoveryStep} based on completed steps:`,
        completedSteps,
      );

      // Only show error toast if this is clearly a critical failure
      if (currentStep < 1 || currentStep > totalSteps + 2) {
        // Allow some buffer for async updates
        toast({
          variant: "destructive",
          title: "Navigation Error",
          description: `Step validation error. Recovering to step ${recoveryStep}.`,
        });
      }

      // Try graceful recovery first instead of full reset
      if (recoveryStep > 0 && recoveryStep <= totalSteps) {
        setData((prevData) => ({
          ...prevData,
          onboarding_meta: {
            ...prevData.onboarding_meta!,
            current_step: recoveryStep,
          },
        }));
      } else {
        // Only reset as last resort
        console.error("Graceful recovery failed, performing full reset");
        resetOnboarding();
      }
    }
  }, [
    currentStep,
    totalSteps,
    organization?.id,
    isLoading,
    isNavigating,
    data.onboarding_meta?.completed_steps,
    toast,
    resetOnboarding,
  ]);

  // Memoize context value to prevent unnecessary re-renders
  const value: OnboardingContextType = useMemo(
    () => ({
      data,
      currentStep,
      totalSteps,
      isLoading,
      isNavigating,
      updateData,
      nextStep,
      previousStep,
      completeOnboarding,
    }),
    [
      data,
      currentStep,
      totalSteps,
      isLoading,
      isNavigating,
      updateData,
      nextStep,
      previousStep,
      completeOnboarding,
    ],
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error("useOnboarding must be used within an OnboardingProvider");
  }
  return context;
}
